function _getEvents() {
  const U = window.umbrellaConfig || {};
  return U.events || {};
};

function _dispatchEvent(eventKey, fallback, detail = {}) {
  const configured = _getEvents()[eventKey];
  const names = new Set([fallback]);
  if(configured) names.add(configured);
  names.forEach((name) => {
    document.dispatchEvent(new CustomEvent(name, { detail, bubbles: true }));
  });
};

export async function request(url, options = {}) {
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if(!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const data = await response.json();
      message = (data && (data.description || data.message)) || message;
    } catch (e) {};
    const err = new Error(message);
    err.status = response.status;
    throw err;
  };
  return await response.json();
};

export async function getCart(sectionId) {
  if(!sectionId) throw new Error('sectionId is required');
  const url = `${window.location.pathname}?section_id=${sectionId}`;
  const response = await fetch(url, { headers: { 'Accept': 'text/html' } });
  if(!response.ok) {
    const err = new Error(`Failed to fetch section (${response.status})`);
    err.status = response.status;
    throw err;
  };
  return await response.text();
};

const LINE_ITEM_PROPERTY_FIELD = /^properties\[(.+)]$/;

/**
 * Line item properties from fields named `properties[Some key]` (gift cards, personalization, etc.).
 * Uses FormData iteration — same successful controls as a real submit (excludes disabled fields).
 * Duplicate keys: last wins. File inputs are skipped.
 * @param {FormData} formData
 * @returns {Record<string, string>|undefined}
 */
export function lineItemPropertiesFromFormData(formData) {
  if(!formData) return undefined;
  const properties = {};
  for(const [key, val] of formData.entries()) {
    const m = LINE_ITEM_PROPERTY_FIELD.exec(key);
    if(!m) continue;
    if(val instanceof File) continue;
    const str = typeof val === 'string' ? val : String(val);
    if(str.trim() === '') continue;
    properties[m[1]] = str;
  };
  return Object.keys(properties).length ? properties : undefined;
};

/**
 * @param {Record<string, string>|FormData|HTMLFormElement|null|undefined} source
 * @returns {Record<string, string>|undefined}
 */
function normalizeLineItemProperties(source) {
  if(source == null) return undefined;
  if(source instanceof FormData) return lineItemPropertiesFromFormData(source);
  if(source instanceof HTMLFormElement) return lineItemPropertiesFromFormData(new FormData(source));
  if(typeof source === 'object' && !Array.isArray(source)) {
    const out = {};
    for(const [k, v] of Object.entries(source)) {
      if(v == null || String(v).trim() === '') continue;
      out[k] = String(v);
    };
    return Object.keys(out).length ? out : undefined;
  };
  return undefined;
};

/**
 * @param {string|number} variantId
 * @param {number} quantity
 * @param {Record<string, string>|FormData|HTMLFormElement|undefined} [propertiesSource] — object, FormData, or form element; any `properties[…]` entries become Cart API `properties`
 */
export async function addItemToCart(variantId, quantity = 1, propertiesSource = undefined) {
  if(!variantId || !quantity) return;
  const properties = normalizeLineItemProperties(propertiesSource);
  const item = await request('/cart/add.js', {
    method: 'POST',
    body: JSON.stringify({
      id: variantId,
      quantity,
      ...(properties ? { properties } : {}),
    }),
  });
  _dispatchEvent('cartAdded', 'cart:added', { item });
  return item;
};

export async function removeItemFromCart(productId) {
  if(!productId) return;
  await request('/cart/change.js', {
    method: 'POST',
    body: JSON.stringify({ id: productId, quantity: 0 }),
  });
  _dispatchEvent('cartItemRemoved', 'cart:removed', { id: productId });
  return true;
};

export async function clearCart() {
  await request('/cart/clear.js', {
    method: 'POST',
  });
  _dispatchEvent('cartCleared', 'cart:cleared', {});
};

export async function updateCart(productId, quantity) {
  if(!productId || typeof quantity !== 'number') return;
  await request('/cart/change.js', {
    method: 'POST',
    body: JSON.stringify({ id: productId, quantity }),
  });
  _dispatchEvent('cartItemUpdated', 'cart:updated', { id: productId, quantity });
  return true;
};

